import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Divider,
  Drawer,
  Flex,
  Popconfirm,
  Space,
  Typography,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import Title from "antd/es/typography/Title";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api";
import Loader from "../../../components/Loader";
import {
  AdminDeactivePolicyReason,
  EnabledLanguage,
} from "../../../domain/types";
import { useAppSelector } from "../../../store";
import { AppState } from "../../../store/appSlice";
import AdminLangSwicher from "../../admin_components/AdminLangSwicher";

const { Text } = Typography;

const AdminPolicyDeactivationReasons = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { field_Language } = useAppSelector(
    (data: { app: AppState }) => data.app
  );

  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [drawerStatus, setDrawerStatus] = useState<boolean>(false);
  const [existingReason, setExistingReason] = useState<
    Partial<AdminDeactivePolicyReason>
  >({});

  const { data: productCategories, isLoading: isProductCategoryLoading } =
    api.useGetAdminProductCategoriesListQuery(undefined);

  const categoryId = productCategories?.find((x) => x.slug === slug)?.id || 0;

  const {
    data: deactiveReasonArr,
    isLoading: isDeaactiveReasonArrLoading,
    refetch: refetchReasons,
  } = api.useGetAdminPolicyDeactiveReasonQuery({
    categoryId,
  });

  const {
    data: deactiveReasonDetail,
    isLoading: isDeaactiveReasonDetailLoading,
    refetch: refetchReasonDetail,
  } = api.useGetAdminPolicyDeactiveReasonDetailQuery(
    selectedReasonId as number,
    {
      skip: !selectedReasonId,
    }
  );

  const [storePolicyReason, { isLoading: isStorePolicyReason }] =
    api.useStorePolicyReasonMutation();

  const [
    removeReason,
    { isLoading: isRemoveReasonLoading, isSuccess: removeReasonSuccess },
  ] = api.useRemoveReasonMutation();

  const onCloseDrawer = () => {
    setDrawerStatus(false);
    setSelectedReasonId(null);
    setExistingReason({});
  };

  const setEditReasonTranslationField = (
    field: keyof AdminDeactivePolicyReason,
    language: EnabledLanguage,
    value: any
  ) => {
    setExistingReason({
      ...existingReason,
      translations: {
        ...existingReason.translations,
        [language]: {
          ...existingReason?.translations?.[language],
          [field]: value,
        },
      },
    });
  };

  useEffect(() => {
    if (deactiveReasonDetail) {
      setExistingReason(deactiveReasonDetail);
    } else if (selectedReasonId) {
      // Reset existingReason when selectedReasonId changes but data hasn't loaded yet
      setExistingReason({});
    }
  }, [deactiveReasonDetail, selectedReasonId]);

  // Refetch detail when drawer opens with a selected reason
  useEffect(() => {
    if (drawerStatus && selectedReasonId) {
      refetchReasonDetail();
    }
  }, [drawerStatus, selectedReasonId, refetchReasonDetail]);

  useEffect(() => {
    if (removeReasonSuccess) {
      refetchReasons();
      onCloseDrawer();
    }
  }, [removeReasonSuccess, refetchReasons]);

  const storeReasonFun = async (isActive?: boolean) => {
    try {
      if (selectedReasonId === null) {
        await storePolicyReason({
          productCategoryId: categoryId,
          translations: existingReason.translations,
        }).unwrap();
        toast.success(`${t("createdSuccessfully")}`);
      } else {
        await storePolicyReason({
          ...existingReason,
          isActive: isActive,
        }).unwrap();
        toast.success(`${t("updatedSuccessfully")}`);
      }
      refetchReasons();
      onCloseDrawer();
    } catch (error: any) {
      if (Array.isArray(error?.data?.errors) && error.data.errors.length > 0) {
        const firstError = error.data.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error(`${t("errorOccurred")}`);
      }
    }
  };

  const removeReasonFunction = async () => {
    if (selectedReasonId === null) return;

    try {
      await removeReason(selectedReasonId).unwrap();
      refetchReasons();
      onCloseDrawer();
    } catch (error: any) {
      if (Array.isArray(error?.data?.errors) && error.data.errors.length > 0) {
        const firstError = error.data.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error(`${t("errorOccurred")}`);
      }
    }
  };

  const MainLoader =
    isDeaactiveReasonArrLoading ||
    isProductCategoryLoading ||
    isDeaactiveReasonDetailLoading ||
    isStorePolicyReason ||
    isRemoveReasonLoading;

  return MainLoader ? (
    <Loader />
  ) : (
    <>
      <Flex justify="space-between">
        <Button
          size="large"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setDrawerStatus(true);
            setSelectedReasonId(null);
            setExistingReason({});
          }}
        >
          {t("create")}
        </Button>

        <AdminLangSwicher />
      </Flex>

      <Divider />

      <Flex gap={20} wrap="wrap" justify="space-evenly">
        {deactiveReasonArr?.map((item, index) => (
          <Badge.Ribbon
            key={index}
            style={{
              marginTop: "-20px",
            }}
            text={item.isActive ? t("active") : t("inActive")}
            color={item.isActive ? "green" : "red"}
          >
            <Card
              hoverable
              style={{
                maxWidth: "350px",
                width: "100vw",
                display: "grid",
                placeItems: "center",
                height: "100%",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setExistingReason({});
                setSelectedReasonId(item.id);
                setDrawerStatus(true);
              }}
            >
              <Title level={5}>
                {item.translations?.[field_Language]?.title ||
                  t("textNotFound")}
              </Title>
            </Card>
          </Badge.Ribbon>
        ))}
      </Flex>

      <Drawer
        extra={<AdminLangSwicher />}
        title={selectedReasonId ? t("update") : t("create")}
        onClose={onCloseDrawer}
        open={drawerStatus}
      >
        <Flex vertical gap={20}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text>{t("deactivationReason")}</Text>
            <Badge.Ribbon
              text={t("deactivationReason")}
              color="blue"
              style={{
                marginTop: "-20px",
              }}
            >
              <TextArea
                size="large"
                placeholder={t("deactivationReason")}
                autoSize={{ minRows: 2 }}
                value={
                  existingReason.translations?.[field_Language]?.title || ""
                }
                onChange={(event) =>
                  setEditReasonTranslationField(
                    "title",
                    field_Language,
                    event?.target?.value
                  )
                }
              />
            </Badge.Ribbon>
          </Space>

          <Divider />

          <Flex vertical gap={10}>
            <Button
              size="large"
              type="primary"
              onClick={() => storeReasonFun()}
              style={{ width: "100%" }}
            >
              {selectedReasonId ? t("update") : t("create")}
            </Button>

            {selectedReasonId && (
              <>
                {existingReason.isActive !== false && (
                  <Button
                    size="large"
                    danger
                    onClick={() => storeReasonFun(false)}
                    style={{ width: "100%" }}
                  >
                    {t("deactivate")}
                  </Button>
                )}

                {existingReason.isActive === false && (
                  <Button
                    size="large"
                    onClick={() => storeReasonFun(true)}
                    style={{ width: "100%" }}
                  >
                    {t("activate")}
                  </Button>
                )}

                <Popconfirm
                  title={t("certainToDeleteIt")}
                  onConfirm={removeReasonFunction}
                  okText="Yes"
                  cancelText="No"
                  placement="bottom"
                >
                  <Button size="large" danger style={{ width: "100%" }}>
                    <DeleteOutlined />
                  </Button>
                </Popconfirm>
              </>
            )}
          </Flex>
        </Flex>
      </Drawer>
    </>
  );
};

export default AdminPolicyDeactivationReasons;
